import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Seo from "./Seo";

const sections = [
  {
    title: "1. Titolare del trattamento",
    body: [
      "Il titolare del trattamento dei dati personali è Arcadia Gioielli S.r.l., con sede in Via Tevere 140, 52043 Castiglion Fiorentino (AR), P.IVA 02340230511, email: info@arcadiaoro.it.",
      "Ma.Vi è un brand di Arcadia Gioielli S.r.l.",
    ],
  },
  {
    title: "2. Dati personali raccolti",
    body: [
      "Attraverso il modulo d'ordine del sito raccogliamo: nome, cognome, indirizzo email, numero di telefono, indirizzo di spedizione e codice fiscale.",
      "I dati di pagamento (carta o conto PayPal) NON transitano né vengono salvati sui nostri sistemi: il pagamento avviene direttamente sui server sicuri di PayPal.",
      "Raccogliamo inoltre le preferenze di personalizzazione del gioiello (collezione, modello, soggetti, incisioni richieste).",
    ],
  },
  {
    title: "3. Finalità e base giuridica del trattamento",
    body: [
      "I dati sono trattati per: a) evasione dell'ordine e creazione del gioiello personalizzato (esecuzione del contratto, art. 6.1.b GDPR); b) adempimento degli obblighi fiscali e contabili, per cui è richiesto il codice fiscale (obbligo legale, art. 6.1.c GDPR); c) comunicazioni relative all'ordine via email o WhatsApp; d) risposta a richieste di contatto.",
      "Il conferimento dei dati è necessario per completare l'ordine: in loro mancanza non potremo evadere la richiesta.",
    ],
  },
  {
    title: "4. Modalità del trattamento e conservazione",
    body: [
      "I dati sono trattati con strumenti informatici e conservati in forma elettronica con misure di sicurezza adeguate.",
      "I dati fiscali e contabili sono conservati per 10 anni come previsto dalla normativa italiana; i dati di contatto per le comunicazioni sull'ordine sono conservati per il tempo necessario alla gestione del rapporto e comunque non oltre 24 mesi dall'ultimo contatto.",
    ],
  },
  {
    title: "5. Comunicazione a terzi",
    body: [
      "I dati possono essere comunicati a: corrieri e spedizionieri per la consegna; PayPal (PayPal Europe S.à r.l.) per l'elaborazione del pagamento; consulenti fiscali e contabili per gli adempimenti di legge; fornitori di servizi tecnici che gestiscono il sito.",
      "I dati non sono venduti né ceduti a terzi per finalità di marketing.",
    ],
  },
  {
    title: "6. I tuoi diritti",
    body: [
      "Ai sensi degli artt. 15-22 del GDPR (Reg. UE 2016/679) hai diritto di: accedere ai tuoi dati, chiederne la rettifica o la cancellazione, limitarne o opporti al trattamento, richiedere la portabilità dei dati.",
      "Per esercitare i tuoi diritti scrivi a info@arcadiaoro.it. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).",
    ],
  },
  {
    title: "7. Cookie",
    body: [
      "Questo sito utilizza esclusivamente cookie tecnici necessari al funzionamento (ad esempio per la sicurezza e le preferenze di sessione). Non utilizziamo cookie di profilazione propri; possono essere presenti cookie di terze parti legati a servizi esterni (PayPal, Instagram) secondo le rispettive informative.",
    ],
  },
  {
    title: "8. Modifiche a questa informativa",
    body: [
      "La presente informativa può essere aggiornata nel tempo. La versione vigente è quella pubblicata su questa pagina.",
      "Ultimo aggiornamento: ottobre 2026.",
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FDF8F7]" data-testid="privacy-page">
      <Seo
        title="Privacy Policy — Ma.Vi Gioielli"
        description="Informativa privacy di Ma.Vi Gioielli (Arcadia Gioielli S.r.l.): come trattiamo i dati dei tuoi ordini, pagamenti PayPal, diritti GDPR e contatti."
      />
      <header className="border-b border-[#F2DBD8] bg-[#FDF8F7]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" data-testid="privacy-logo">
            <img src="/logo.png" alt="Ma.Vi Gioielli" className="h-12 w-auto" />
          </a>
          <a href="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#6B5459] hover:text-[#C4758A] transition-colors" data-testid="privacy-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C4758A]">Informativa GDPR</span>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-4 text-[#37282B]" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-4 text-base text-[#6B5459] leading-relaxed">
            Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (“GDPR”)
            e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018.
          </p>
        </motion.div>

        <div className="mt-12 space-y-10">
          {sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.05 * i }}
              className="bg-white rounded-2xl border border-[#F2DBD8] p-7 sm:p-8"
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className="font-serif-display text-2xl text-[#37282B]">{s.title}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-3 text-sm sm:text-base text-[#6B5459] leading-relaxed">
                  {p}
                </p>
              ))}
            </motion.section>
          ))}
        </div>

        <p className="mt-12 text-center text-xs text-[#A89095]" data-testid="privacy-footer-note">
          Ma.Vi è un brand di Arcadia Gioielli S.r.l. — Via Tevere 140, 52043 Castiglion Fiorentino (AR) — P.IVA 02340230511
        </p>
      </main>
    </div>
  );
}
